"use client";

import { useEffect, useMemo, useState } from "react";
import { Overline } from "@repo/ui/overline";
import { RichTextCondensed } from "@/components/common/rich-text/rich-text-condensed.component";
import type { RichTextCondensed as RichTextCondensedSchema } from "@/lib/sanity/sanity.types";
interface StatisticItemProps {
  value: string;
  shouldAnimate: boolean;
  headline?: string;
  description?: RichTextCondensedSchema;
}
const DURATION_MS = 2600;
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
interface ParsedValue {
  prefix: string;
  suffix: string;
  numeric: number;
  hasCommas: boolean;
  decimals: number;
}
function parseValue(value: string): ParsedValue | null {
  const match = value.match(/^(?<prefix>\D*)(?<raw>[\d,]+(?:\.\d+)?)(?<suffix>.*)$/);
  const raw = match?.groups?.raw;
  if (!raw) return null;
  const numeric = Number.parseFloat(raw.replace(/,/g, ""));
  if (Number.isNaN(numeric)) return null;
  return {
    prefix: match?.groups?.prefix ?? "",
    suffix: match?.groups?.suffix ?? "",
    numeric,
    hasCommas: raw.includes(","),
    decimals: raw.split(".")[1]?.length ?? 0
  };
}
function formatNumber(current: number, hasCommas: boolean, decimals: number) {
  const fixed = current.toFixed(decimals);
  if (!hasCommas) return fixed;
  const [int = "0", dec] = fixed.split(".");
  const withCommas = int.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return dec ? `${withCommas}.${dec}` : withCommas;
}
function render(parsed: ParsedValue, current: number) {
  return `${parsed.prefix}${formatNumber(current, parsed.hasCommas, parsed.decimals)}${parsed.suffix}`;
}
export function StatisticItem({
  value,
  shouldAnimate,
  headline,
  description
}: StatisticItemProps) {
  const parsed = useMemo(() => parseValue(value), [value]);
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    if (!parsed || !shouldAnimate) return;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const duration = reducedMotion ? 0 : DURATION_MS;
    let raf = 0;
    const start = performance.now();
    const tick = (now: number) => {
      const t = duration === 0 ? 1 : Math.min((now - start) / duration, 1);
      setProgress(easeOutCubic(t));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [parsed, shouldAnimate]);
  const display = parsed ? render(parsed, parsed.numeric * progress) : value;
  return <div className="flex flex-col gap-6 desktop:gap-10" data-testid="statistic-item">
      <p className="typography-mobile-display-d2 desktop:typography-desktop-display-d2 text-text-primary tabular-nums">
        {display}
      </p>
      {(headline || description) && <div className="flex flex-col gap-2 max-w-[315px] desktop:max-w-[320px]">
          {headline && <Overline icon={false} as="p" variant="default">
              {headline}
            </Overline>}
          {description && <RichTextCondensed richText={description} className="text-text-secondary" />}
        </div>}
    </div>;
}